import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  IconButton,
  Divider,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { theme as appTheme } from '../../theme/theme';

const PrivacyPolicyScreen: React.FC = () => {
  const navigate = useNavigate();

  // Responsive styles for consistent typography
  const sectionTitleStyles = {
    mb: { xs: 1.5, sm: 2 },
    fontWeight: 'bold',
    color: appTheme.colors.primary,
    fontSize: { xs: '1.25rem', sm: '1.5rem' },
    lineHeight: { xs: 1.3, sm: 1.334 },
  };

  const bodyTextStyles = {
    lineHeight: { xs: 1.6, sm: 1.7 },
    fontSize: { xs: '0.875rem', sm: '1rem' },
    textAlign: 'justify' as const,
  };

  const listItemStyles = {
    mb: { xs: 0.5, sm: 1 },
    fontSize: { xs: '0.875rem', sm: '1rem' },
    lineHeight: { xs: 1.5, sm: 1.6 },
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, var(--primary-purple) 0%, var(--gradient-purple) 50%, var(--primary-purple) 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <Box sx={{ mb: 3 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              color: '#FFFFFF',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <ArrowBack />
          </IconButton>
        </Box>

        <Paper
          elevation={8}
          sx={{
            p: { xs: 2, sm: 3, md: 4, lg: 5 },
            borderRadius: 3,
            backgroundColor: '#FFFFFF',
            maxWidth: '100%',
            overflow: 'hidden',
          }}
        >
          <Typography
            variant="h3"
            sx={{
              textAlign: 'center',
              mb: { xs: 3, sm: 4 },
              color: appTheme.colors.primary,
              fontWeight: 'bold',
              fontSize: { xs: '1.75rem', sm: '2.125rem', md: '2.5rem' },
              lineHeight: { xs: 1.2, sm: 1.167 },
            }}
          >
            隐私政策
          </Typography>

          <Typography 
            variant="body2" 
            sx={{ 
              mb: { xs: 2, sm: 3 }, 
              color: 'text.secondary',
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              textAlign: 'center',
            }}
          >
            最近更新：{new Date().toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' })}
          </Typography>

          <Divider sx={{ mb: { xs: 3, sm: 4 } }} />

          <Box sx={{ '& > *': { mb: { xs: 2, sm: 3 } } }}>
            <Box>
              <Typography variant="h5" sx={sectionTitleStyles}>
                1. 引言
              </Typography>
              <Typography variant="body1" sx={bodyTextStyles}>
                紫微AI（以下简称“我们”）重视并保护您的隐私。本《隐私政策》说明我们在您使用本占星指导平台及相关服务时如何收集、使用、披露并保护您的个人信息。
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" sx={sectionTitleStyles}>
                2. 我们收集的信息
              </Typography>
              <Typography variant="body1" sx={{ ...bodyTextStyles, mb: { xs: 1.5, sm: 2 } }}>
                我们会收集您直接提供给我们的信息，包括：
              </Typography>
              
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: { xs: 0.75, sm: 1 }, 
                  fontWeight: 'bold',
                  fontSize: { xs: '1rem', sm: '1.125rem' },
                }}
              >
                个人信息：
              </Typography>
              <Box component="ul" sx={{ pl: { xs: 2, sm: 3 }, mb: { xs: 1.5, sm: 2 } }}>
                <Typography component="li" variant="body1" sx={listItemStyles}>用于创建账户的姓名与邮箱</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}>用于排盘的出生日期、时间与地点</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}>用于订阅或购买的支付信息</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}>通信偏好与设置</Typography>
              </Box>

              <Typography 
                variant="h6" 
                sx={{ 
                  mb: { xs: 0.75, sm: 1 }, 
                  fontWeight: 'bold',
                  fontSize: { xs: '1rem', sm: '1.125rem' },
                }}
              >
                使用信息：
              </Typography>
              <Box component="ul" sx={{ pl: { xs: 2, sm: 3 }, mb: { xs: 1.5, sm: 2 } }}>
                <Typography component="li" variant="body1" sx={listItemStyles}>设备信息与 IP 地址</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}>浏览器类型与操作系统</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}>访问页面与使用的功能</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}>在平台停留的时间</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}>与 AI 咨询功能的交互行为</Typography>
              </Box>
            </Box>

            <Box>
              <Typography variant="h5" sx={sectionTitleStyles}>
                3. 我们如何使用您的信息
              </Typography>
              <Typography variant="body1" sx={{ ...bodyTextStyles, mb: { xs: 1.5, sm: 2 } }}>
                我们使用所收集的信息以：
              </Typography>
              <Box component="ul" sx={{ pl: { xs: 2, sm: 3 } }}>
                <Typography component="li" variant="body1" sx={listItemStyles}>生成个性化的占星排盘与解读</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}>提供定制的运势预测与指导</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}>处理支付并管理订阅</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}>改进 AI 算法与服务质量</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}>发送账户与服务的重要通知</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}>提供客服支持与回复咨询</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}>保障平台安全并防止欺诈</Typography>
              </Box>
            </Box>

            <Box>
              <Typography variant="h5" sx={sectionTitleStyles}>
                4. 信息共享与披露
              </Typography>
              <Typography variant="body1" sx={{ ...bodyTextStyles, mb: { xs: 1.5, sm: 2 } }}>
                除以下情况外，我们不会向第三方出售、交换或转让您的个人信息：
              </Typography>
              <Box component="ul" sx={{ pl: { xs: 2, sm: 3 } }}>
                <Typography component="li" variant="body1" sx={listItemStyles}><strong>服务提供商：</strong>为运营平台、处理支付或提供客服支持，我们可能与可信的第三方共享信息</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}><strong>法律要求：</strong>当法律要求或为保护我们的权利、财产或安全时，我们可能披露信息</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}><strong>业务转移：</strong>在合并、收购或资产出售时，您的信息可能作为交易的一部分被转移</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}><strong>您的同意：</strong>在获得您明确同意的情况下，我们可能共享信息</Typography>
              </Box>
            </Box>

            <Box>
              <Typography variant="h5" sx={sectionTitleStyles}>
                5. 数据安全
              </Typography>
              <Typography variant="body1" sx={bodyTextStyles}>
                我们采取适当的技术与组织安全措施，以防止未经授权的访问、篡改、披露或破坏您的个人信息，包括：
              </Typography>
              <Box component="ul" sx={{ mt: { xs: 1.5, sm: 2 }, pl: { xs: 2, sm: 3 } }}>
                <Typography component="li" variant="body1" sx={listItemStyles}>敏感数据在传输及存储时进行加密</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}>定期进行安全评估与更新</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}>访问控制与身份验证措施</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}>通过可信服务提供商进行安全的支付处理</Typography>
              </Box>
            </Box>

            <Box>
              <Typography variant="h5" sx={sectionTitleStyles}>
                6. 数据保留
              </Typography>
              <Typography variant="body1" sx={bodyTextStyles}>
                我们仅在为您提供服务及履行本隐私政策所述目的所必需的期间内保留您的个人信息。具体而言：
              </Typography>
              <Box component="ul" sx={{ mt: { xs: 1.5, sm: 2 }, pl: { xs: 2, sm: 3 } }}>
                <Typography component="li" variant="body1" sx={listItemStyles}>账户信息将在您的账户有效期内保留</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}>占星排盘与解读结果将被存储以提供持续服务</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}>支付信息将根据法律要求及为防止欺诈而保留</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}>使用数据可能以聚合、匿名的形式保留，用于服务改进</Typography>
              </Box>
            </Box>

            <Box>
              <Typography variant="h5" sx={sectionTitleStyles}>
                7. 您的权利与选择
              </Typography>
              <Typography variant="body1" sx={{ ...bodyTextStyles, mb: { xs: 1.5, sm: 2 } }}>
                您对您的个人信息享有以下权利：
              </Typography>
              <Box component="ul" sx={{ pl: { xs: 2, sm: 3 } }}>
                <Typography component="li" variant="body1" sx={listItemStyles}><strong>访问权：</strong>请求获取我们持有的关于您的个人信息副本</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}><strong>更正权：</strong>请求更正不准确或不完整的信息</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}><strong>删除权：</strong>在符合法律规定的前提下，请求删除您的个人信息</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}><strong>可携权：</strong>请求将您的数据转移至其他服务提供商</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}><strong>选择退出权：</strong>随时退订营销通讯</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}><strong>账户删除权：</strong>通过账户设置删除您的账户及相关数据</Typography>
              </Box>
            </Box>

            <Box>
              <Typography variant="h5" sx={sectionTitleStyles}>
                8. Cookie 与追踪技术
              </Typography>
              <Typography variant="body1" sx={bodyTextStyles}>
                我们使用 Cookie 及类似的追踪技术以提升您在我们平台上的体验。这些技术帮助我们：
              </Typography>
              <Box component="ul" sx={{ mt: { xs: 1.5, sm: 2 }, pl: { xs: 2, sm: 3 } }}>
                <Typography component="li" variant="body1" sx={listItemStyles}>记住您的偏好与设置</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}>分析使用模式以改进我们的服务</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}>提供个性化内容与推荐</Typography>
                <Typography component="li" variant="body1" sx={listItemStyles}>保障平台安全并防止欺诈</Typography>
              </Box>
              <Typography variant="body1" sx={{ ...bodyTextStyles, mt: { xs: 1.5, sm: 2 } }}>
                您可以通过浏览器偏好设置来管理 Cookie，但这可能会影响平台部分功能的正常使用。
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" sx={sectionTitleStyles}>
                9. 第三方服务
              </Typography>
              <Typography variant="body1" sx={bodyTextStyles}>
                我们的平台可能集成第三方服务以用于支付处理、数据分析及其他功能。这些服务拥有其独立的隐私政策，我们建议您查阅。我们对这些第三方服务的隐私实践不承担责任。
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" sx={sectionTitleStyles}>
                10. 国际数据传输
              </Typography>
              <Typography variant="body1" sx={bodyTextStyles}>
                您的信息可能被传输至并处理于您居住国以外的国家。我们确保此类传输符合适用的数据保护法律，并采取适当的保障措施来保护您的信息。
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" sx={sectionTitleStyles}>
                11. 儿童隐私
              </Typography>
              <Typography variant="body1" sx={bodyTextStyles}>
                我们的服务不面向未满18周岁的个人。我们不会有意收集未满18周岁儿童的个人信息。如果我们发现已收集此类信息，将立即采取措施予以删除。
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" sx={sectionTitleStyles}>
                12. 本隐私政策的变更
              </Typography>
              <Typography variant="body1" sx={bodyTextStyles}>
                我们可能会不时更新本隐私政策，以反映我们的实践或适用法律的变化。对于重大变更，我们会在平台发布更新版本并通过邮件通知您。在此类变更后继续使用我们的服务，即表示您接受更新后的隐私政策。
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" sx={sectionTitleStyles}>
                13. 联系我们
              </Typography>
              <Typography variant="body1" sx={bodyTextStyles}>
                如果您对本隐私政策或我们的数据实践有任何疑问、疑虑或请求，请通过以下方式联系我们：
              </Typography>
              <Box sx={{ 
                mt: { xs: 1.5, sm: 2 }, 
                p: { xs: 1.5, sm: 2 }, 
                backgroundColor: '#f5f5f5', 
                borderRadius: 1 
              }}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    mb: { xs: 0.5, sm: 1 },
                  }}
                >
            {/* 已移除邮箱，改为官网链接以避免泄露个人信息 */}
            <strong>官网：</strong> https://ziwei.jieqirili.com
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    mb: { xs: 0.5, sm: 1 },
                  }}
                >

                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PrivacyPolicyScreen;